import {
  Button,
  COLORS,
  Checkbox,
  FONT_SIZES,
  FONT_WEIGHTS,
  RadioButton,
  RadioGroup,
  Text,
  TextInput,
  UnstyledButton,
  mediaTabletAndUp,
  tabletAndUp,
} from '@aboutyou/ui-components';
import styled, {css} from 'styled-components';
import {CloudflareTurnstile} from '../../../Captcha/Captcha';

export const ForgotPasswordContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

export const StyledForgotPassword = styled(Text)`
  display: inline-block;
  text-align: right;
  margin-top: -8px;
  margin-bottom: 0;
  cursor: pointer;
`;

export const SigninTextInputContainer = styled.div<{$columnLayout?: boolean}>`
  display: flex;
  flex-direction: column;

  ${({$columnLayout}) =>
    $columnLayout &&
    css`
      flex-direction: row;
      margin-top: 0;
    `}
`;

export const SubmitButton = styled(Button)`
  && {
    margin: 1.5rem auto 0;

    ${tabletAndUp(css`
      max-width: 260px;
    `)}
  }
`;

export const FormFieldsWrapper = styled.div`
  padding-bottom: 100px;

  @media ${mediaTabletAndUp} {
    padding-bottom: 0;
  }
`;

export const StyledCloudflareTurnstile = styled(CloudflareTurnstile)`
  display: flex;
  justify-content: center;
  margin: auto;
  margin-top: 2rem;
  height: 64px; // height of turnstile element -- behaves as skeleton
`;

export const StyledTextInput = styled(TextInput)`
  margin-bottom: ${({errorMessage}) => (errorMessage ? '10px' : '25px')};
`;

export const StyledSigninSignupTextInput = styled(StyledTextInput)<{
  columnLayout?: boolean;
}>`
  flex: 1;
  margin-right: 0;
  margin-bottom: 12px;
  min-height: 48px;

  ${({columnLayout}) =>
    columnLayout &&
    css`
      margin-right: 16px;

      &:last-child {
        margin-right: 0;
      }
    `}
`;

const getAlignment = (side: 'left' | 'right' | 'center' = 'center') =>
  css`
    text-align: ${side};
  `;

interface HintTextProps {
  error?: boolean;
  mgBottomLg?: boolean;
  inline?: boolean;
  align?: 'left' | 'right' | 'center';
}

export const HintText = styled.span<HintTextProps>`
  font-size: ${FONT_SIZES.px12};
  font-weight: 500;
  margin: ${({mgBottomLg}) => (mgBottomLg ? '1.5rem 0 2rem' : '1.5rem 0')};
  color: ${({error}) => (error ? COLORS.Red : COLORS.Grey1)};
  display: ${({inline}) => (inline ? `inline` : `block`)};
  line-height: 1.33;
  ${props => getAlignment(props.align)}
`;

export const SignUpOverlay = styled.div`
  position: absolute;
  z-index: 1;
  background: rgba(255, 255, 255, 0.7);
  left: 0;
  top: 0;
  height: 100%;
  width: 100%;
  cursor: not-allowed;
`;

export const LegalText = styled.div`
  font-size: ${FONT_SIZES.px10};
  font-weight: ${FONT_WEIGHTS.mediumBold};
  margin: 2rem 0 0;
  color: ${COLORS.Grey1};
  text-align: center;
  line-height: 1.6;
  div {
    margin-top: 0.2rem;
  }

  a {
    line-height: 1.6;
    color: ${COLORS.Grey1};
    margin: 0 0.2rem;
    border-bottom: solid 1px ${COLORS.Grey1};
    text-decoration: none;
  }
`;

export const FormRow = styled.div<{$columnLayout?: boolean}>`
  display: flex;
  flex-direction: column;

  ${({$columnLayout}) =>
    $columnLayout &&
    css`
      flex-direction: row;
    `}
`;

const LabelCSS = css<{$hideLines?: boolean}>`
  && {
    font-weight: ${FONT_WEIGHTS.mediumBold};
    line-height: 1.43;
    color: ${COLORS.Grey1};
  }

  ${({$hideLines}) =>
    $hideLines &&
    css`
      overflow: hidden;
      -webkit-line-clamp: 2;
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-box-orient: vertical;
    `}

  a,
  span {
    text-decoration: underline;
    color: ${COLORS.Grey1};
  }
`;

export const LabelText = styled.div<{$hideLines?: boolean}>`
  ${LabelCSS}

  font-size: ${FONT_SIZES.px14};
`;

export const ShowMoreButton = styled(UnstyledButton)<{$hideLines?: boolean}>`
  ${LabelCSS}

  && {
    font-size: ${FONT_SIZES.px12};
  }
`;

export const StyledGenderContainer = styled.div<{$columnLayout?: boolean}>`
  display: flex;
  flex-direction: column;
  margin-top: 12px;
  margin-bottom: 20px;

  ${({$columnLayout}) =>
    $columnLayout &&
    css`
      flex-direction: row;
      justify-content: space-between;
      margin-bottom: 0;
    `}
`;

export const StyledRadioGroupLabel = styled.label`
  font-size: ${FONT_SIZES.px14};
  font-weight: ${FONT_WEIGHTS.mediumBold};
  line-height: 18px;
  margin-bottom: 20px;
`;

export const StyledRadioButton = styled(RadioButton)<{
  $columnLayout?: boolean;
}>`
  display: flex;
  justify-content: ${({$columnLayout}) =>
    $columnLayout ? 'flex-end' : 'flex-start'};

  span {
    flex: 1;
    color: ${COLORS.Black};
    font-weight: ${FONT_WEIGHTS.bold};
    padding-left: 8px;
  }
`;

export const CenterElements = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const DarkerText = styled(HintText)`
  font-size: ${FONT_SIZES.px10};
  color: ${COLORS.Black};
  display: inline;
  margin: 0 0.3rem;
  cursor: pointer;
  border-bottom: 1px solid ${COLORS.Black};
`;

export const GuestSessionExpander = styled.div`
  font-weight: ${FONT_WEIGHTS.mediumBold};
  height: 66px;
  font-size: ${FONT_SIZES.px14};
  font-stretch: normal;
  font-style: normal;
  line-height: 1.29;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: -1rem;
  color: ${COLORS.Grey1};
  cursor: pointer;
  @media ${mediaTabletAndUp} {
    margin-bottom: -2rem;
  }
`;

export const StyledRadioGroup = styled(RadioGroup)`
  justify-content: flex-start;
  flex-wrap: wrap;
  gap: 12px;
  > label {
    min-width: 0;
  }
`;

export const StyledNewsletterCheckbox = styled(Checkbox)`
  ${LabelText} {
    font-size: 12px;
    line-height: 18px;
    font-weight: ${FONT_WEIGHTS.mediumBold};
  }

  input {
    &:checked {
      & ~ ${LabelText} {
        font-size: 12px;
        line-height: 18px;
        font-weight: ${FONT_WEIGHTS.mediumBold};
      }
    }
  }
`;

export const CancelButton = styled.div`
  font-size: ${FONT_SIZES.px14};
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 1rem;
  color: ${COLORS.Black};
  cursor: pointer;
`;

export const StyledRecognizedUserForm = styled.form<{$tall?: boolean}>`
  position: relative;

  @media ${mediaTabletAndUp} {
    height: calc(100vh - 56px);
    height: ${({$tall}) => ($tall ? 580 : 480)}px;
    width: auto;
    box-sizing: border-box;
  }
`;

export const TextCentered = styled.div<{
  $smaller?: boolean;
  $heading?: boolean;
  $noMargin?: boolean;
}>`
  font-size: ${FONT_SIZES.px16};
  font-weight: bold;
  text-align: center;
  line-height: 1.5;

  ${({$noMargin}) =>
    !$noMargin &&
    css`
      margin: 1.5rem 0;
    `}

  ${({$smaller}) =>
    $smaller &&
    css`
      font-size: 14px;
      font-weight: ${FONT_WEIGHTS.mediumBold};
    `}

  ${({$heading}) =>
    $heading &&
    css`
      font-size: 20px;
      line-height: 1.3;
    `}
`;

export const WelcomeHeading = styled.div`
  font-size: ${FONT_SIZES.px24};
  line-height: 28px;
  letter-spacing: 0.3px;
  margin-bottom: 16px;
`;

export const GrayedText = styled(HintText)`
  font-size: ${FONT_SIZES.px14};
  line-height: 18px;
  color: ${COLORS.Grey1};
  cursor: pointer;
  margin-top: 24px;
  margin-bottom: 32px;
`;

export const NoAccountButton = styled(UnstyledButton)`
  width: 100%;
`;

export const MoveToBottom = styled.div`
  border-top: 1px solid ${COLORS.Grey3};

  @media ${mediaTabletAndUp} {
    position: absolute;
    bottom: 0;
    width: 100%;
    text-align: center;
    left: 0;

    ${GrayedText} {
      height: 66px;
      line-height: 66px;
      width: 100%;
      margin: 0;

      &:hover {
        text-decoration: underline;
      }
    }
  }
`;
